/** V5.1 → V2 — Chat IA de qualification (facultatif, après la demande de rappel). */
import { Bot } from "lucide-react";
import { isDAv2 } from "@/lib/da";
import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QualifChat({ simulationId, prenom, config, onCompleted, onFallback }) {
  const [qid, setQid] = useState(() => { try { return sessionStorage.getItem(`cf:sim:qual:${simulationId}`) || null; } catch { return null; } });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo?.({ top: 999999, behavior: "smooth" });
  }, [messages, sending]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.post(`${API}/public/qualification/start`, { simulation_id: simulationId });
        if (!r.data.ok) { setError("fallback"); onFallback(); return; }
        setQid(r.data.qualification_id);
        try { sessionStorage.setItem(`cf:sim:qual:${simulationId}`, r.data.qualification_id); } catch { /* noop */ }
        setMessages(r.data.messages || []);
        if (r.data.status === "completed") { onCompleted(); }
      } catch {
        setError("fallback"); onFallback();
      } finally {
        setLoading(false);
      }
    })();
  }, [simulationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending || !qid) return;
    setSending(true);
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text, at: new Date().toISOString() }]);
    try {
      const r = await axios.post(`${API}/public/qualification/message`, { qualification_id: qid, text });
      if (!r.data.ok && r.data.fallback) { onFallback(); return; }
      if (r.data.messages && r.data.messages.length) {
        setMessages((prev) => [...prev, ...r.data.messages]);
      }
      if (r.data.status === "completed") {
        setTimeout(onCompleted, 800);
      }
    } catch {
      onFallback();
    } finally {
      setSending(false);
    }
  };

  if (loading) return (
    <div className="pt-8 text-center" data-testid="sim-qualif-loading">
      <div className="animate-pulse text-slate-400 text-sm">L'assistant se prépare…</div>
    </div>
  );

  if (error === "fallback") return null;

  return (
    <div className="flex flex-col" style={{ minHeight: "calc(100vh - 140px)" }} data-testid="sim-qualif">
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[color:var(--cf-primary-text)]">Qualification</div>
        <h2 className="font-extrabold text-slate-900 text-[20px] leading-tight">Discussion avec l'assistant de {config?.display_name || "Teddy"}</h2>
      </div>
      <div ref={scrollRef} className="flex-1 space-y-3 pb-4 pt-1 overflow-y-auto" data-testid="sim-qualif-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" && (
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-extrabold shrink-0 mr-2" style={{ background: "var(--cf-gradient)" }}>
                {isDAv2() ? <Bot size={16} /> : "🤖"}
              </div>
            )}
            <div
              className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-[14px] leading-snug whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-[color:var(--cf-primary)] text-white rounded-br-sm"
                  : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
              }`}
              data-testid={`sim-qualif-msg-${m.role}-${i}`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start" data-testid="sim-qualif-typing">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-extrabold shrink-0 mr-2" style={{ background: "var(--cf-gradient)" }}>{isDAv2() ? <Bot size={16} /> : "🤖"}</div>
            <div className="max-w-[80%] px-3.5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-500 text-[13px]">
              <span className="inline-flex gap-1">
                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="sticky bottom-0 bg-white/95 backdrop-blur-md border-t border-slate-100 py-3 -mx-4 px-4" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}>
        <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ta réponse…"
            className="flex-1 min-w-0 px-4 py-3 rounded-full bg-white border-2 border-slate-200 focus:border-[color:var(--cf-primary)] outline-none text-[14px]"
            disabled={sending}
            data-testid="sim-qualif-input"
            autoFocus
          />
          <button
            type="submit"
            disabled={!input.trim() || sending}
            className="w-11 h-11 rounded-full flex items-center justify-center text-white text-lg font-extrabold shadow-md disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition"
            style={{ background: "var(--cf-gradient)" }}
            data-testid="sim-qualif-send"
          >
            →
          </button>
        </form>
      </div>
    </div>
  );
}
